import React, { forwardRef, useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../app/tokens.js";
import { CornerTable } from "./CornerTable.jsx";
import { TABLE_TOP_CORNERS, attachGridHeaderCorners, useTableHeaderCornerScope } from "./tableHeaderCorners.js";
import { useHorizontalOverflow } from "../hooks/useHorizontalOverflow.js";

function scrollOwner(element) {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    if (node.classList.contains("v2-content")) break;
    if (/auto|scroll/.test(getComputedStyle(node).overflowX)) return node;
  }
  return null;
}

export function OverflowButton({ overflow, right = 0 }) {
  if (!overflow.canRight) return null;
  return (
    <button type="button" aria-label="Show more columns" title="Show more columns"
      data-list-overflow-button=""
      onClick={(event) => { event.stopPropagation(); overflow.scrollRight(); }}
      style={{
        position: "absolute", right, top: 0, bottom: 0, width: 34,
        padding: "0 4px 0 10px", display: "flex", alignItems: "center", justifyContent: "center",
        border: 0, background: T.listOverflowFade, color: T.listAccent,
        cursor: "pointer", zIndex: 6,
      }}>
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
        <polyline points="9 6 15 12 9 18" />
      </svg>
    </button>
  );
}

function rowBackground(cell) {
  for (let node = cell.parentElement; node; node = node.parentElement) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && bg !== "transparent" && bg !== T.listTransparent) return bg;
  }
  return T.listCard;
}

// The same adapter also serves the Offices grid. Selectors are local to this
// explicit owner, never a global DOM enhancer. Split headers use the BODY ref.
export function useListOverflow({ element, scrollRef, pinLast = false, grid = false, invalidate }) {
  const [localOwner, setLocalOwner] = useState(null);
  const [header, setHeader] = useState(null);
  const [actionWidth, setActionWidth] = useState(0);
  const overflow = useHorizontalOverflow({ scrollRef: scrollRef || { current: localOwner }, content: element, invalidate });
  useLayoutEffect(() => { setLocalOwner(scrollOwner(element)); }, [element]);
  useLayoutEffect(() => {
    if (!element) return undefined;
    const head = () => grid ? element.querySelector('[role="row"]') : element.tHead?.rows[0];
    let resize = null;
    let observedHeader = null;
    const update = () => {
      const row = head();
      const last = row?.lastElementChild;
      setHeader(last || null);
      if (last) {
        if (resize && last !== observedHeader) {
          if (observedHeader) resize.unobserve?.(observedHeader);
          resize.observe(last);
          observedHeader = last;
        }
        last.style.position = "relative";
        if (overflow.canRight) last.removeAttribute("aria-hidden");
        const ownBackground = getComputedStyle(last).backgroundColor;
        const headerBackground = ownBackground && ownBackground !== "transparent" && ownBackground !== T.listTransparent
          ? ownBackground : rowBackground(last);
        last.style.setProperty("--list-overflow-tint", headerBackground);
        if (pinLast) {
          const width = last.getBoundingClientRect().width;
          setActionWidth((prev) => prev === width ? prev : width);
          const headerClip = grid
            ? (row.parentElement === element ? (overflow.owner || element) : row.parentElement)
            : element.parentElement;
          last.style.transform = "none";
          const clip = headerClip.getBoundingClientRect();
          const delta = Math.max(0, last.getBoundingClientRect().right
            + headerClip.scrollLeft - (overflow.owner?.scrollLeft || 0) - clip.right);
          last.style.transform = `translateX(-${delta}px)`;
          last.style.zIndex = "4";
          last.style.background = headerBackground;
          last.style.borderTopRightRadius = "var(--list-header-radius, 0px)";
          last.style.boxShadow = overflow.canRight ? T.listOverflowSeam : "none";
          last.dataset.listPinned = "header";
        }
      }
      if (pinLast) {
        const rows = grid ? element.querySelectorAll('[data-list-row]') : element.querySelectorAll(":scope > tbody > tr");
        for (const bodyRow of rows) {
          const cell = bodyRow.lastElementChild;
          if (!cell || cell.colSpan > 1) continue;
          cell.dataset.listPinned = "body";
          cell.style.position = "sticky";
          cell.style.right = "0px";
          cell.style.zIndex = "2";
          cell.style.background = rowBackground(cell);
          cell.style.boxShadow = overflow.canRight ? T.listOverflowSeam : "none";
        }
      }
    };
    resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    resize?.observe(element);
    if (overflow.owner) resize?.observe(overflow.owner);
    update();
    const mutation = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList"
        || record.target.matches("tr, [data-list-row]"))) update();
    });
    mutation.observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "aria-selected"] });
    let frame;
    const refresh = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    element.addEventListener("pointerover", refresh);
    element.addEventListener("pointerout", refresh);
    element.addEventListener("focusin", refresh);
    element.addEventListener("focusout", refresh);
    overflow.owner?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      resize?.disconnect(); mutation.disconnect();
      cancelAnimationFrame(frame);
      element.removeEventListener("pointerover", refresh);
      element.removeEventListener("pointerout", refresh);
      element.removeEventListener("focusin", refresh);
      element.removeEventListener("focusout", refresh);
      overflow.owner?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [element, grid, pinLast, overflow.owner, overflow.canRight, invalidate]);
  let right = actionWidth;
  if (!pinLast && header && overflow.owner) {
    right = Math.max(0, header.getBoundingClientRect().right - overflow.owner.getBoundingClientRect().right);
  }
  return { overflow, header, right };
}

// Native table semantics and the parent's scroll/sticky ownership are retained.
export const OverflowTable = forwardRef(function OverflowTable(
  { children, overflowScrollRef, pinActions = false, ...props }, forwardedRef,
) {
  const [element, setElement] = useState(null);
  const ref = useCallback((node) => {
    setElement(node);
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);
  const { overflow, header, right } = useListOverflow({
    element, scrollRef: overflowScrollRef, pinLast: pinActions, invalidate: children,
  });
  return (
    <CornerTable {...props} ref={ref} data-list-overflow="">
      {children}
      {header && createPortal(<OverflowButton overflow={overflow} right={right} />, header)}
    </CornerTable>
  );
});

function ScopedTable({ element, owner, pinActions }) {
  const { overflow, header, right } = useListOverflow({
    element, scrollRef: owner ? { current: owner } : undefined, pinLast: pinActions,
  });
  return header ? createPortal(<OverflowButton overflow={overflow} right={right} />, header) : null;
}

const NO_OPTIONS = {};
// Explicit list owners opt in; observe only their local subtree to support
// asynchronous/conditional lists. Never walk the document or enhance routes.
export function ListOverflowScope({ children, options = NO_OPTIONS }) {
  const [root, setRoot] = useState(null);
  const [tables, setTables] = useState([]);
  useTableHeaderCornerScope(root);
  useLayoutEffect(() => {
    if (!root) return undefined;
    const discover = () => {
      const next = Array.from(root.querySelectorAll(options.selector || "table"))
        .filter((table) => table.closest("[data-list-scope]") === root
          && !table.hasAttribute("data-list-overflow") && !table.closest("[data-list-overflow]")
          && (!options.exclude || !table.matches(options.exclude))
          && (table.tHead || options.split?.some((pair) => table.matches(pair.bodyTable))))
        .map((element) => {
          const label = element.tHead?.rows[0]?.lastElementChild?.textContent?.trim() || "";
          const actions = options.pinActions ?? (/^(actions?|open|record|links?)$/i.test(label)
            || (!label && Boolean(element.querySelector("tbody tr > td:last-child button, tbody tr > td:last-child a"))));
          return { element, actions: Boolean(actions) };
        });
      setTables((prev) => prev.length === next.length && prev.every((table, i) =>
        table.element === next[i].element && table.actions === next[i].actions) ? prev : next);
    };
    discover();
    const observer = new MutationObserver(discover);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [root, options]);
  return (
    <div ref={setRoot} data-list-scope="" style={{ display: "contents" }}>
      {children}
      {tables.map(({ element: table, actions }, i) => {
        const split = options.split?.find((pair) => table.matches(pair.header) || table.matches(pair.bodyTable));
        const owner = split ? root.querySelector(split.body) : null;
        return <ScopedTable key={i} element={table} owner={owner} pinActions={Boolean(actions)} />;
      })}
    </div>
  );
}

export function withListOverflow(Component, options = NO_OPTIONS) {
  function ListOwner(props) {
    return <ListOverflowScope options={options}><Component {...props} /></ListOverflowScope>;
  }
  ListOwner.displayName = `ListOverflow(${Component.name})`;
  return ListOwner;
}

export function OverflowGrid({ children, minWidth, ...props }) {
  const [element, setElement] = useState(null);
  const [owner, setOwner] = useState(null);
  useLayoutEffect(() => attachGridHeaderCorners(element?.querySelector('[role="row"]'), owner), [element, owner]);
  const { overflow, header, right } = useListOverflow({
    element, scrollRef: { current: owner }, pinLast: true, grid: true,
  });
  return (
    <div ref={setOwner} style={{ overflowX: "auto", ...TABLE_TOP_CORNERS }}>
      <div {...props} ref={setElement} style={{ ...props.style, ...TABLE_TOP_CORNERS, minWidth }}>
        {children}
        {header && createPortal(<OverflowButton overflow={overflow} right={right} />, header)}
      </div>
    </div>
  );
}
